/**
 * useAuth — Authentication hook
 *
 * Access token in memory (api-client setToken/getToken); refresh token in
 * localStorage['ledger-rt']. Silent refresh runs before expiry. The store also
 * tracks the active Workspace, Book, and the user's switchable Workspaces.
 */

import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { disablePush } from '../lib/push.js';
import {
  setToken,
  getToken,
  getTokenVersion,
  newIdempotencyKey,
} from '@budgeting101/api-client';
import { create } from 'zustand';
import { queryClient } from '../lib/queryClient.js';
import { useWizardStore } from '../store/wizardStore.js';

let preservedDraftOwnerUserId: string | null = null;

/**
 * Cached financial data and in-memory drafts belong to one user in one
 * workspace. A workspace switch suspends the active draft but preserves its
 * namespaced recovery record. An identity boundary also purges persisted
 * drafts so nothing leaks across accounts sharing a device.
 */
function suspendWorkspaceScopedState() {
  queryClient.clear();
  useWizardStore.getState().suspendDraft();
}

function purgeIdentityScopedState() {
  preservedDraftOwnerUserId = null;
  queryClient.clear();
  useWizardStore.getState().purgeDrafts();
}

type WorkspaceProfileKind = 'SHARED' | 'CONTROL' | 'OPERATIONS' | 'MONEY';
export type WorkspaceStatus = 'ACTIVE' | 'BLOCKED' | 'ARCHIVED';
export type WorkspaceBookKind = 'GROUP_OBLIGATION' | 'FORMAL_ACCOUNTING' | 'SECONDARY_ACCOUNTING';
export type WorkspaceCapabilityState =
  | 'UNAVAILABLE'
  | 'AVAILABLE'
  | 'ENABLED'
  | 'CONFIGURING'
  | 'BLOCKED'
  | 'LEGACY_UNREVIEWED'
  | 'READY'
  | 'SUSPENDED'
  | 'ARCHIVED';

export interface WorkspaceCapability {
  key: string;
  state: WorkspaceCapabilityState;
}

export interface ActiveWorkspaceBook {
  id: string;
  name: string;
  kind: WorkspaceBookKind;
  status: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  readiness: 'LEGACY_UNREVIEWED' | 'CONFIGURING' | 'READY' | 'BLOCKED';
  currency: string | null;
}

export interface Workspace {
  workspaceId: string;
  workspaceName: string;
  workspaceStatus?: WorkspaceStatus;
  profileKinds: WorkspaceProfileKind[];
  companyId: string | null;
  companyName: string | null;
  roleName: string;
}

export interface RegisterPayload {
  companyName: string;
  baseCurrency: string;
  name: string;
  email: string;
  password: string;
}

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: string;
  permissions?: string[];
  platformAdmin?: boolean;
}

interface SessionSnapshot {
  user: AuthUser;
  workspaceId: string;
  workspaceName: string;
  profileKinds: WorkspaceProfileKind[];
  capabilities: WorkspaceCapability[];
  companyId: string | null;
  companyName: string | null;
  baseCurrency: string | null;
  workspaces: Workspace[];
}

interface AuthState {
  userId:   string | null;
  userName: string | null;
  userEmail: string | null;
  platformAdmin: boolean;
  role:     string | null;
  permissions: string[];
  workspaceId: string | null;
  workspaceName: string | null;
  profileKinds: WorkspaceProfileKind[];
  capabilities: WorkspaceCapability[];
  activeBook: ActiveWorkspaceBook | null;
  contextRevision: number;
  companyId: string | null;
  companyName: string | null;
  baseCurrency: string | null;
  workspaces: Workspace[];
  isAuthenticated: boolean;
  isTransitioning: boolean;
  setUser:  (user: AuthUser | null) => void;
  setWorkspaceContext: (ctx: {
    workspaceId?: string | null;
    workspaceName?: string | null;
    profileKinds?: WorkspaceProfileKind[];
    capabilities?: WorkspaceCapability[];
    companyId: string | null;
    companyName: string | null;
    baseCurrency?: string | null;
    workspaces: Workspace[];
  }) => void;
  beginTransition: () => void;
  cancelTransition: () => void;
  commitSession: (session: SessionSnapshot) => void;
  commitIdentitySession: (user: AuthUser) => void;
  setActiveBook: (book: ActiveWorkspaceBook | null) => void;
  updateCompanyName: (name: string) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  userId:          null,
  userName:        null,
  userEmail:       null,
  platformAdmin:   false,
  role:            null,
  permissions:     [],
  workspaceId:     null,
  workspaceName:   null,
  profileKinds:    [],
  capabilities:    [],
  activeBook:      null,
  contextRevision: 0,
  companyId:       null,
  companyName:     null,
  baseCurrency:    null,
  workspaces:      [],
  isAuthenticated: false,
  isTransitioning: false,

  setUser(user) {
    if (user) {
      set({
        userId:          user.id,
        userName:        user.name,
        userEmail:       user.email,
        platformAdmin:   user.platformAdmin ?? false,
        role:            user.role,
        permissions:     user.permissions ?? [],
        isAuthenticated: true,
        isTransitioning: false,
      });
    } else {
      preservedDraftOwnerUserId = null;
      set({
        userId: null, userName: null, userEmail: null, platformAdmin: false, role: null, permissions: [],
        workspaceId: null, workspaceName: null, profileKinds: [], capabilities: [], activeBook: null,
        companyId: null, companyName: null, baseCurrency: null, workspaces: [],
        isAuthenticated: false,
        isTransitioning: false,
      });
    }
  },

  setWorkspaceContext(ctx) {
    set((current) => ({
      workspaceId: ctx.workspaceId ?? ctx.companyId,
      workspaceName: ctx.workspaceName ?? ctx.companyName,
      profileKinds: ctx.profileKinds ?? [],
      capabilities: ctx.capabilities ?? [],
      activeBook: current.workspaceId === (ctx.workspaceId ?? ctx.companyId) ? current.activeBook : null,
      companyId: ctx.companyId,
      companyName: ctx.companyName,
      baseCurrency: ctx.baseCurrency ?? null,
      workspaces: ctx.workspaces,
    }));
  },

  beginTransition() {
    set({ isTransitioning: true });
  },

  cancelTransition() {
    set({ isTransitioning: false });
  },

  commitSession(session) {
    set((current) => ({
      userId: session.user.id,
      userName: session.user.name,
      userEmail: session.user.email,
      platformAdmin: session.user.platformAdmin ?? false,
      role: session.user.role,
      permissions: session.user.permissions ?? [],
      workspaceId: session.workspaceId,
      workspaceName: session.workspaceName,
      profileKinds: session.profileKinds,
      capabilities: session.capabilities,
      activeBook: null,
      contextRevision: current.contextRevision + 1,
      companyId: session.companyId,
      companyName: session.companyName,
      baseCurrency: session.baseCurrency,
      workspaces: session.workspaces,
      isAuthenticated: true,
      isTransitioning: false,
    }));
  },

  /** Signed in with zero reachable workspaces: identity only. The router
   * sends this state to /start so the user can rebuild from onboarding. */
  commitIdentitySession(user) {
    set((current) => ({
      userId: user.id,
      userName: user.name,
      userEmail: user.email,
      platformAdmin: user.platformAdmin ?? false,
      role: null,
      permissions: [],
      workspaceId: null,
      workspaceName: null,
      profileKinds: [],
      capabilities: [],
      activeBook: null,
      contextRevision: current.contextRevision + 1,
      companyId: null,
      companyName: null,
      baseCurrency: null,
      workspaces: [],
      isAuthenticated: true,
      isTransitioning: false,
    }));
  },

  setActiveBook(book) {
    set((current) => {
      const active = current.activeBook;
      if (
        active?.id === book?.id
        && active?.name === book?.name
        && active?.kind === book?.kind
        && active?.status === book?.status
        && active?.readiness === book?.readiness
        && active?.currency === book?.currency
      ) return current;
      return { activeBook: book };
    });
  },

  updateCompanyName(name) {
    set((current) => ({
      companyName: name,
      workspaceName: name,
      workspaces: current.workspaces.map((workspace) => (
        workspace.workspaceId === current.workspaceId
          || (current.companyId !== null && workspace.companyId === current.companyId)
          ? { ...workspace, workspaceName: name, companyName: name }
          : workspace
      )),
    }));
  },
}));

/** Boot: single-flight silent refresh (tokens rotate on use). */
let refreshInFlight: Promise<boolean> | null = null;
let credentialRotationTail: Promise<void> = Promise.resolve();
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let authGeneration = 0;

/**
 * Refresh tokens rotate on every successful use. Queue every silent refresh
 * and workspace switch so a switch reads the token produced by the preceding
 * refresh instead of racing it with the same one-time credential.
 */
function serializeCredentialRotation<T>(operation: () => Promise<T>): Promise<T> {
  const result = credentialRotationTail.then(operation, operation);
  credentialRotationTail = result.then(() => undefined, () => undefined);
  return result;
}

/**
 * A rotation may commit server-side even when its response is lost. Retry one
 * network failure with the same command identity so the server can replay the
 * original receipt instead of consuming the credential twice.
 */
async function postRefresh(
  payload: { refreshToken: string; workspaceId?: string },
): Promise<{ response: Response; replayedAfterNetwork: boolean }> {
  const idempotencyKey = newIdempotencyKey();
  const body = JSON.stringify(payload);
  const request = () => fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    },
    body,
  });
  try {
    return { response: await request(), replayedAfterNetwork: false };
  } catch {
    return { response: await request(), replayedAfterNetwork: true };
  }
}

function scheduleSilentRefresh(delay = 14 * 60_000) {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(attemptSilentRefresh, delay);
}

const MAX_BROWSER_TIMER_DELAY_MS = 2_147_483_647;

export function silentRefreshDelay(expiresAt: string, now = Date.now()): number {
  const expiry = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiry)) return 0;
  return Math.min(Math.max(expiry - now - 60_000, 0), MAX_BROWSER_TIMER_DELAY_MS);
}

function clearRefreshTimer() {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
}

function clearLocalSession(options: { preserveWorkspaceDrafts?: boolean } = {}) {
  const draftOwnerUserId = useAuthStore.getState().userId ?? preservedDraftOwnerUserId;
  authGeneration++;
  clearRefreshTimer();
  setToken(null);
  localStorage.removeItem('ledger-rt');
  useAuthStore.getState().setUser(null);
  if (options.preserveWorkspaceDrafts) {
    preservedDraftOwnerUserId = draftOwnerUserId;
    suspendWorkspaceScopedState();
  } else {
    purgeIdentityScopedState();
  }
}

async function clearServerSession(refreshToken: string | null): Promise<void> {
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
  } catch {
    // Local invalidation remains authoritative while the API is unavailable.
  }
}

async function clearUnverifiableSession(options: {
  preserveWorkspaceDrafts?: boolean;
  refreshToken?: string | null;
} = {}): Promise<void> {
  const refreshToken = options.refreshToken ?? localStorage.getItem('ledger-rt');
  clearLocalSession({ preserveWorkspaceDrafts: options.preserveWorkspaceDrafts });
  // Refresh and login responses set an HttpOnly access cookie. Explicitly
  // clear/revoke it before claiming that an uncertain browser session ended.
  await clearServerSession(refreshToken);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function normalizeProfileKinds(value: unknown): WorkspaceProfileKind[] {
  if (!Array.isArray(value)) return [];
  return value.filter((kind): kind is WorkspaceProfileKind => (
    kind === 'SHARED' || kind === 'CONTROL' || kind === 'OPERATIONS' || kind === 'MONEY'
  ));
}

function normalizeCapabilityState(value: unknown): WorkspaceCapabilityState | null {
  if (typeof value !== 'string') return null;
  return [
    'UNAVAILABLE',
    'AVAILABLE',
    'ENABLED',
    'CONFIGURING',
    'BLOCKED',
    'LEGACY_UNREVIEWED',
    'READY',
    'SUSPENDED',
    'ARCHIVED',
  ].includes(value) ? value as WorkspaceCapabilityState : null;
}

/**
 * Normalize both the canonical Workspace directory contract and the previous
 * Control-only shape. Legacy rows derive Workspace identity from Company only;
 * a Shared row must provide its canonical Workspace fields.
 */
function normalizeWorkspace(value: unknown): Workspace | null {
  if (!isObject(value)) return null;
  const companyId = optionalString(value.companyId);
  const companyName = optionalString(value.companyName);
  const workspaceId = optionalString(value.workspaceId) ?? companyId;
  const workspaceName = optionalString(value.workspaceName) ?? companyName;
  if (!workspaceId || !workspaceName) return null;
  const workspaceStatus = value.workspaceStatus === 'BLOCKED' || value.workspaceStatus === 'ARCHIVED'
    ? value.workspaceStatus
    : 'ACTIVE';
  const profileKinds = normalizeProfileKinds(value.profileKinds);
  return {
    workspaceId,
    workspaceName,
    workspaceStatus,
    profileKinds: profileKinds.length > 0 ? profileKinds : companyId ? ['CONTROL'] : [],
    companyId,
    companyName,
    roleName: optionalString(value.roleName) ?? 'member',
  };
}

function normalizeWorkspaceList(value: unknown): Workspace[] {
  if (!Array.isArray(value)) return [];
  const byId = new Map<string, Workspace>();
  for (const candidate of value) {
    const workspace = normalizeWorkspace(candidate);
    if (workspace) byId.set(workspace.workspaceId, workspace);
  }
  return [...byId.values()];
}

async function fetchCompleteWorkspaceDirectory(accessToken: string): Promise<Workspace[] | null> {
  const items: unknown[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | null = null;

  do {
    if (cursor) {
      if (seenCursors.has(cursor)) return null;
      seenCursors.add(cursor);
    }
    const search = new URLSearchParams({ limit: '100' });
    if (cursor) search.set('cursor', cursor);
    const response = await fetch(`/api/auth/workspaces?${search.toString()}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) return null;
    const page = await response.json().catch(() => null) as unknown;
    if (!isObject(page) || !Array.isArray(page.items)) return null;
    items.push(...page.items);
    if (page.nextCursor !== null && page.nextCursor !== undefined && typeof page.nextCursor !== 'string') {
      return null;
    }
    cursor = optionalString(page.nextCursor);
  } while (cursor);

  return normalizeWorkspaceList(items);
}

interface IdentityProbe {
  user: AuthUser;
  activeWorkspaceId: string | null;
}

/** Identity-level session check: distinguishes "signed in with no workspace"
 * from "workspace session" before any workspace hydration is attempted. */
async function loadIdentityProbe(accessToken: string): Promise<IdentityProbe | null> {
  const response = await fetch('/api/auth/identity', {
    credentials: 'include',
    headers: { Authorization: `Bearer ${accessToken}` },
  }).catch(() => null);
  if (!response?.ok) return null;
  const payload = await response.json().catch(() => null) as unknown;
  if (!isObject(payload) || !isObject(payload.user)) return null;
  const id = optionalString(payload.user.id);
  const name = optionalString(payload.user.name);
  const email = optionalString(payload.user.email);
  const activeWorkspaceId = payload.activeWorkspaceId;
  if (
    !id
    || !name
    || !email
    || !Object.prototype.hasOwnProperty.call(payload, 'activeWorkspaceId')
    || (activeWorkspaceId !== null && typeof activeWorkspaceId !== 'string')
  ) return null;
  return {
    user: {
      id,
      name,
      email,
      role: 'none',
      platformAdmin: payload.user.platformAdmin === true,
    },
    activeWorkspaceId,
  };
}

/** Read and validate target context without mutating credentials or live state. */
async function loadSessionSnapshot(accessToken: string): Promise<SessionSnapshot | null> {
  const meRes = await fetch('/api/auth/me', {
    credentials: 'include',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!meRes.ok) return null;
  const me = await meRes.json().catch(() => null) as unknown;
  if (!isObject(me) || !isObject(me.user)) return null;

  const userId = optionalString(me.user.id);
  const userName = optionalString(me.user.name);
  const userEmail = optionalString(me.user.email);
  if (!userId || !userName || !userEmail) return null;

  const company = isObject(me.company) ? me.company : null;
  const companyId = optionalString(company?.id);
  const companyName = optionalString(company?.name);
  const workspace = isObject(me.workspace) ? me.workspace : null;
  const workspaceId = optionalString(workspace?.id) ?? companyId;
  const workspaceName = optionalString(workspace?.name) ?? companyName;
  if (!workspaceId || !workspaceName) return null;

  const role = isObject(me.role) ? me.role : null;
  const roleName = optionalString(role?.name) ?? 'member';
  const permissions = Array.isArray(role?.permissions)
    ? role.permissions.filter((permission): permission is string => typeof permission === 'string')
    : [];
  const profileKinds = normalizeProfileKinds(workspace?.profiles);
  const activeContext = isObject(me.activeContext) ? me.activeContext : null;
  const rawCapabilities = Array.isArray(activeContext?.capabilities) ? activeContext.capabilities : [];
  const capabilities = rawCapabilities.flatMap((value) => {
    if (!isObject(value)) return [];
    const key = optionalString(value.key);
    const state = normalizeCapabilityState(value.state);
    return key && state ? [{ key, state }] : [];
  });

  let workspaces = normalizeWorkspaceList(me.workspaces);
  if (me.workspacesTruncated === true) {
    const completeDirectory = await fetchCompleteWorkspaceDirectory(accessToken);
    if (!completeDirectory) return null;
    workspaces = completeDirectory;
  }
  if (!workspaces.some((candidate) => candidate.workspaceId === workspaceId)) {
    workspaces.unshift({
      workspaceId,
      workspaceName,
      workspaceStatus: workspace?.status === 'BLOCKED' || workspace?.status === 'ARCHIVED'
        ? workspace.status
        : 'ACTIVE',
      profileKinds: profileKinds.length > 0 ? profileKinds : companyId ? ['CONTROL'] : [],
      companyId,
      companyName,
      roleName,
    });
  }

  return {
    user: {
      id: userId,
      name: userName,
      email: userEmail,
      role: roleName,
      permissions,
      platformAdmin: me.user.platformAdmin === true,
    },
    workspaceId,
    workspaceName,
    profileKinds: profileKinds.length > 0 ? profileKinds : companyId ? ['CONTROL'] : [],
    capabilities,
    companyId,
    companyName,
    baseCurrency: optionalString(company?.baseCurrency),
    workspaces,
  };
}

interface AuthErrorPayload {
  message?: string;
  code?: string;
  error?: { message?: string; code?: string };
}

async function authRequestError(response: Response, fallback: string) {
  const payload = await response.json().catch(() => ({})) as AuthErrorPayload;
  const error = new Error(
    response.status === 429
      ? 'Too many attempts. Please try again shortly.'
      : payload.error?.message ?? payload.message ?? fallback,
  ) as Error & { code?: string; status: number };
  error.code = payload.error?.code ?? payload.code;
  error.status = response.status;
  return error;
}

export function attemptSilentRefresh(): Promise<boolean> {
  refreshInFlight ??= serializeCredentialRotation(doSilentRefresh)
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

async function doSilentRefresh(): Promise<boolean> {
  const generation = authGeneration;
  const rt = localStorage.getItem('ledger-rt');
  if (!rt) return false;
  try {
    const attempt = await postRefresh({ refreshToken: rt });
    const res = attempt.response;
    if (!res.ok) {
      if (attempt.replayedAfterNetwork && generation === authGeneration) {
        await clearUnverifiableSession({
          preserveWorkspaceDrafts: true,
          refreshToken: rt,
        });
        return false;
      }
      if (localStorage.getItem('ledger-rt') === rt) localStorage.removeItem('ledger-rt');
      return false;
    }
    const data = normalizeTokenResponse(await res.json().catch(() => null));
    if (!data) {
      if (generation === authGeneration) {
        await clearUnverifiableSession({
          preserveWorkspaceDrafts: true,
          refreshToken: rt,
        });
      }
      return false;
    }
    if (generation !== authGeneration) return false;
    const session = await loadSessionSnapshot(data.accessToken);
    if (generation !== authGeneration) return false;
    if (session) {
      installSession(data, session);
      return true;
    }
    // Workspace authentication rejects an identity-only token. Probe at the
    // identity level only after that expected miss so normal boot keeps its
    // established refresh -> /auth/me request sequence.
    const identity = await loadIdentityProbe(data.accessToken);
    if (generation !== authGeneration) return false;
    if (identity?.activeWorkspaceId === null) {
      setToken(data.accessToken);
      localStorage.setItem('ledger-rt', data.refreshToken);
      useAuthStore.getState().commitIdentitySession(identity.user);
      scheduleSilentRefresh();
      return true;
    }
    await clearUnverifiableSession({
      preserveWorkspaceDrafts: true,
      refreshToken: rt,
    });
    return false;
  } catch {
    // postRefresh has already replayed the exact command once. If both
    // responses are lost, the server may have rotated the credential.
    if (generation === authGeneration) {
      await clearUnverifiableSession({
        preserveWorkspaceDrafts: true,
        refreshToken: rt,
      });
    }
    return false;
  }
}

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt?: string;
}

function normalizeTokenResponse(value: unknown): TokenResponse | null {
  if (!isObject(value)) return null;
  const accessToken = optionalString(value.accessToken);
  const refreshToken = optionalString(value.refreshToken);
  if (!accessToken || !refreshToken) return null;
  return {
    accessToken,
    refreshToken,
    ...(typeof value.expiresAt === 'string' ? { expiresAt: value.expiresAt } : {}),
  };
}

/** Install verified credentials and their context as one observable handoff. */
function installSession(data: TokenResponse, session: SessionSnapshot) {
  setToken(data.accessToken);
  localStorage.setItem('ledger-rt', data.refreshToken);
  useAuthStore.getState().commitSession(session);
  if (data.expiresAt) {
    scheduleSilentRefresh(silentRefreshDelay(data.expiresAt));
  } else {
    scheduleSilentRefresh();
  }
}

export function useAuth() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const authStore  = useAuthStore();

  const handleExpired = useCallback((event: Event) => {
    const detail = (event as CustomEvent<unknown>).detail;
    const requestTokenVersion = isObject(detail) && typeof detail.tokenVersion === 'number'
      ? detail.tokenVersion
      : null;
    if (requestTokenVersion !== null && requestTokenVersion !== getTokenVersion()) return;
    // Source requests can finish after a Workspace rotation has revoked their
    // session. Versioned events remain harmless even after target installation.
    if (useAuthStore.getState().isTransitioning) return;
    const refreshToken = localStorage.getItem('ledger-rt');
    clearLocalSession();
    void clearServerSession(refreshToken);
    navigate(`/login?returnTo=${encodeURIComponent(location.pathname)}`, { replace: true });
  }, [navigate, location.pathname]);

  useEffect(() => {
    window.addEventListener('auth:expired', handleExpired as EventListener);
    return () => window.removeEventListener('auth:expired', handleExpired as EventListener);
  }, [handleExpired]);

  const login = useCallback((email: string, password: string) => serializeCredentialRotation(async () => {
    const draftOwnerUserId = useAuthStore.getState().userId ?? preservedDraftOwnerUserId;
    const generation = ++authGeneration;
    clearRefreshTimer();
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });
    if (!res.ok) {
      if (useAuthStore.getState().isAuthenticated) scheduleSilentRefresh();
      throw await authRequestError(res, 'Invalid credentials');
    }
    const rawData = await res.json().catch(() => null) as unknown;
    const data = normalizeTokenResponse(rawData);
    if (!data || generation !== authGeneration) {
      if (generation === authGeneration) await clearUnverifiableSession();
      throw new Error('Signed in, but the session response was invalid. Please sign in again.');
    }
    if (isObject(rawData) && rawData.workspaceId === null) {
      // Zero reachable workspaces: the server issued an identity session.
      // There is no workspace to hydrate — verify the identity (including the
      // platformAdmin flag the login response does not carry), install it, and
      // let the caller route to onboarding or the admin console.
      const identity = await loadIdentityProbe(data.accessToken);
      if (!identity || generation !== authGeneration) {
        if (generation === authGeneration) {
          await clearUnverifiableSession({ refreshToken: data.refreshToken });
        }
        throw new Error('Signed in, but the session response was invalid. Please sign in again.');
      }
      if (draftOwnerUserId && identity.user.id === draftOwnerUserId) {
        preservedDraftOwnerUserId = null;
      } else {
        purgeIdentityScopedState();
      }
      setToken(data.accessToken);
      localStorage.setItem('ledger-rt', data.refreshToken);
      useAuthStore.getState().commitIdentitySession(identity.user);
      scheduleSilentRefresh();
      return rawData;
    }
    // Do not clear a current workspace for a failed sign-in attempt. Once the
    // server accepts the identity, hide the source context while hydration
    // proves whether a deliberately preserved draft belongs to the same user.
    useAuthStore.getState().beginTransition();
    suspendWorkspaceScopedState();
    try {
      const session = await loadSessionSnapshot(data.accessToken);
      if (generation !== authGeneration) throw new Error('Authentication changed');
      if (!session) throw new Error('Workspace hydration failed');
      if (draftOwnerUserId && session.user.id === draftOwnerUserId) {
        preservedDraftOwnerUserId = null;
      } else {
        purgeIdentityScopedState();
      }
      installSession(data, session);
    } catch {
      if (generation === authGeneration) {
        await clearUnverifiableSession({ refreshToken: data.refreshToken });
      }
      throw new Error('Signed in, but the workspace could not be loaded. Please sign in again.');
    }
    return rawData;
  }), []);

  const register = useCallback((payload: RegisterPayload) => serializeCredentialRotation(async () => {
    const generation = ++authGeneration;
    clearRefreshTimer();
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });
    if (!res.ok) {
      if (useAuthStore.getState().isAuthenticated) scheduleSilentRefresh();
      throw await authRequestError(res, 'Could not create your workspace.');
    }
    const rawData = await res.json().catch(() => null) as unknown;
    const data = normalizeTokenResponse(rawData);
    if (!data || generation !== authGeneration) {
      if (generation === authGeneration) await clearUnverifiableSession();
      throw new Error('Your workspace was created, but its session response was invalid. Please sign in again.');
    }
    useAuthStore.getState().beginTransition();
    purgeIdentityScopedState();
    try {
      const session = await loadSessionSnapshot(data.accessToken);
      if (generation !== authGeneration) throw new Error('Authentication changed');
      if (!session) throw new Error('Workspace hydration failed');
      installSession(data, session);
    } catch {
      if (generation === authGeneration) {
        await clearUnverifiableSession({ refreshToken: data.refreshToken });
      }
      throw new Error('Your workspace was created, but it could not be loaded. Please sign in again.');
    }
    return rawData;
  }), []);

  const adoptProvisionedSession = useCallback((tokens: TokenResponse) => (
    serializeCredentialRotation(async () => {
      const generation = ++authGeneration;
      clearRefreshTimer();
      useAuthStore.getState().beginTransition();
      purgeIdentityScopedState();
      try {
        const session = await loadSessionSnapshot(tokens.accessToken);
        if (generation !== authGeneration) throw new Error('Authentication changed');
        if (!session) throw new Error('Workspace hydration failed');
        installSession(tokens, session);
      } catch {
        if (generation === authGeneration) {
          await clearUnverifiableSession({ refreshToken: tokens.refreshToken });
        }
        throw new Error(
          'Your Shared workspace was created, but it could not be opened. Sign in with the owner email instead of creating it again.',
        );
      }
    })
  ), []);

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    await serializeCredentialRotation(async () => {
      // Read only after acquiring the rotation lock: an earlier silent refresh
      // may have replaced the one-time credential while this switch waited.
      const rt = localStorage.getItem('ledger-rt');
      if (!rt) throw new Error('Session expired. Please sign in again.');
      const generation = ++authGeneration;
      clearRefreshTimer();
      useAuthStore.getState().beginTransition();
      suspendWorkspaceScopedState();
      let attempt: { response: Response; replayedAfterNetwork: boolean };
      try {
        attempt = await postRefresh({ refreshToken: rt, workspaceId });
      } catch {
        // The request may have reached the server and revoked the source
        // session even though no response arrived. Do not pair an unverifiable
        // credential with the old cache, but retain its namespaced draft.
        if (generation === authGeneration) {
          await clearUnverifiableSession({
            preserveWorkspaceDrafts: true,
            refreshToken: rt,
          });
        }
        throw new Error('The workspace switch could not be verified. Please sign in again.');
      }
      const res = attempt.response;
      if (!res.ok) {
        const error = await authRequestError(res, 'Could not switch workspace.');
        if (generation === authGeneration) {
          if (attempt.replayedAfterNetwork) {
            await clearUnverifiableSession({
              preserveWorkspaceDrafts: true,
              refreshToken: rt,
            });
          } else {
            useAuthStore.getState().cancelTransition();
            scheduleSilentRefresh(); // keep the current session's refresh cadence alive
          }
        }
        throw attempt.replayedAfterNetwork
          ? new Error('The workspace switch could not be verified. Please sign in again.')
          : error;
      }
      const data = normalizeTokenResponse(await res.json().catch(() => null));
      if (!data) {
        if (generation === authGeneration) {
          await clearUnverifiableSession({
            preserveWorkspaceDrafts: true,
            refreshToken: rt,
          });
        }
        throw new Error('The workspace changed, but its session response was invalid. Please sign in again.');
      }
      try {
        const session = await loadSessionSnapshot(data.accessToken);
        if (generation !== authGeneration) throw new Error('Authentication changed');
        if (!session) throw new Error('Workspace hydration failed');
        installSession(data, session);
      } catch {
        if (generation === authGeneration) {
          await clearUnverifiableSession({
            preserveWorkspaceDrafts: true,
            refreshToken: rt,
          });
        }
        throw new Error('The workspace changed, but its details could not be loaded. Please sign in again.');
      }
      navigate('/', { replace: true });
    });
  }, [navigate]);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('ledger-rt');
    // Revoke the device BEFORE the token is discarded: the unsubscribe call is
    // authenticated. A push endpoint identifies a browser rather than a person,
    // so a subscription left behind would deliver this user's notifications to
    // whoever signs in on this browser next.
    await disablePush().catch(() => undefined);
    clearLocalSession();
    try {
      await clearServerSession(refreshToken);
    } finally {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  return {
    ...authStore,
    token: getToken(),
    login,
    register,
    adoptProvisionedSession,
    switchWorkspace,
    logout,
  };
}
